/**
 * Profile Page
 * Full page component for user profile management
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  KeyIcon,
  ShieldCheckIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/common/SEO';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { ProfileUpdateData } from '../types/auth';
import { getUserCategoryLabel, getUserCategoryIcon } from '../constants/userCategories';

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated, updateProfile, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<ProfileUpdateData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    lineId: user?.lineId || ''
  });

  // Sync formData with user when user data loads
  React.useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        lineId: user.lineId || ''
      });
    }
  }, [user]);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await updateProfile(formData);
      toast.success('บันทึกข้อมูลเรียบร้อยแล้ว');
      setIsEditing(false);
    } catch (error: any) {
      // Get error message from backend response
      // Priority: error.details (most specific) -> message -> fallback
      const errorMessage = error.response?.data?.error?.details ||
                           error.response?.data?.message ||
                           error.response?.data?.detail ||
                           error.message ||
                           'เกิดข้อผิดพลาดในการบันทึกข้อมูล';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      lineId: user?.lineId || ''
    });
    setIsEditing(false);
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get role label
  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
      case 'ADMIN':
        return 'ผู้ดูแลระบบ';
      case 'COMPANY_OWNER':
      case 'MEMBER':
      case 'USER':
        return 'สมาชิก';
      default:
        return 'ผู้ใช้';
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <SEO
        title="ข้อมูลส่วนตัว | Thai Security Directory"
        description="จัดการข้อมูลส่วนตัวของคุณ"
        url="/profile"
      />

      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-8 sm:py-12">
        <div className="container-wrapper max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-neutral-900 mb-2">
                ข้อมูลส่วนตัว
              </h1>
              <p className="text-neutral-600">
                จัดการข้อมูลส่วนตัวและการตั้งค่าบัญชีของคุณ
              </p>
            </div>

            {/* Profile Card */}
            <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
              {/* Profile Header with Avatar */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-8 sm:px-8">
                <div className="flex items-center space-x-4">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.display_name}
                      className="w-20 h-20 rounded-full object-cover ring-4 ring-white shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center ring-4 ring-white shadow-lg">
                      <UserCircleIcon className="w-12 h-12 text-primary-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white mb-1">
                      {user.display_name || user.full_name || 'ผู้ใช้'}
                    </h2>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                        {getRoleLabel(user.role)}
                      </span>
                      {/* Email verification badge removed - users can login immediately */}
                      {/* {user.isEmailVerified && (
                        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-white backdrop-blur-sm">
                          <CheckCircleIcon className="w-3 h-3" />
                          <span>ยืนยันแล้ว</span>
                        </span>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Form */}
              <form onSubmit={handleSubmit} className="p-6 sm:p-8">
                <div className="space-y-6">
                  {/* Email (Read-only) */}
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-neutral-700 mb-2">
                      <EnvelopeIcon className="w-4 h-4" />
                      <span>อีเมล</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={user.email}
                        disabled
                        className="flex-1 px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg text-neutral-500 cursor-not-allowed"
                      />
                      {/* Email verification status removed - all users can login immediately */}
                      {/* {user.isEmailVerified ? (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircleIcon className="w-5 h-5" />
                          <span className="text-sm font-medium">ยืนยันแล้ว</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-amber-600">
                          <XCircleIcon className="w-5 h-5" />
                          <span className="text-sm font-medium">ยังไม่ยืนยัน</span>
                        </div>
                      )} */}
                    </div>
                  </div>

                  {/* User Category (Read-only) */}
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-neutral-700 mb-2">
                      <span>ประเภทผู้ใช้</span>
                    </label>
                    <div className="flex items-center px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg">
                      {user.userCategory ? (
                        <>
                          <span className="text-xl mr-2">{getUserCategoryIcon(user.userCategory)}</span>
                          <span className="text-neutral-700">{getUserCategoryLabel(user.userCategory)}</span>
                        </>
                      ) : (
                        <span className="text-neutral-400">ไม่ระบุ</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-neutral-500">
                      หากต้องการเปลี่ยนประเภทผู้ใช้ กรุณาติดต่อผู้ดูแลระบบ
                    </p>
                  </div>

                  {/* First Name & Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="flex items-center space-x-2 text-sm font-medium text-neutral-700 mb-2">
                        <UserCircleIcon className="w-4 h-4" />
                        <span>ชื่อ</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-colors duration-200 ${
                          isEditing
                            ? 'border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
                            : 'bg-neutral-50 border-neutral-200 text-neutral-600 cursor-not-allowed'
                        }`}
                        placeholder="ชื่อของคุณ"
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="flex items-center space-x-2 text-sm font-medium text-neutral-700 mb-2">
                        <UserCircleIcon className="w-4 h-4" />
                        <span>นามสกุล</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-colors duration-200 ${
                          isEditing
                            ? 'border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
                            : 'bg-neutral-50 border-neutral-200 text-neutral-600 cursor-not-allowed'
                        }`}
                        placeholder="นามสกุลของคุณ"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="flex items-center space-x-2 text-sm font-medium text-neutral-700 mb-2">
                      <PhoneIcon className="w-4 h-4" />
                      <span>เบอร์โทรศัพท์</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-4 py-3 border rounded-lg transition-colors duration-200 ${
                        isEditing
                          ? 'border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
                          : 'bg-neutral-50 border-neutral-200 text-neutral-600 cursor-not-allowed'
                      }`}
                      placeholder="0812345678"
                    />
                  </div>

                  {/* LINE ID */}
                  <div>
                    <label htmlFor="lineId" className="flex items-center space-x-2 text-sm font-medium text-neutral-700 mb-2">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                      </svg>
                      <span>LINE ID</span>
                    </label>
                    <input
                      type="text"
                      id="lineId"
                      name="lineId"
                      value={formData.lineId}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-4 py-3 border rounded-lg transition-colors duration-200 ${
                        isEditing
                          ? 'border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
                          : 'bg-neutral-50 border-neutral-200 text-neutral-600 cursor-not-allowed'
                      }`}
                      placeholder="@yourlineid"
                    />
                  </div>

                  {/* Account Information */}
                  <div className="border-t border-neutral-200 pt-6 mt-6">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                      ข้อมูลบัญชี
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-start space-x-3">
                        <CalendarIcon className="w-5 h-5 text-neutral-400 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-neutral-700">
                            สมัครสมาชิกเมื่อ
                          </div>
                          <div className="text-sm text-neutral-600">
                            {formatDate(user.createdAt)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <CalendarIcon className="w-5 h-5 text-neutral-400 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-neutral-700">
                            เข้าสู่ระบบล่าสุด
                          </div>
                          <div className="text-sm text-neutral-600">
                            {formatDate(user.lastLoginAt)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <ShieldCheckIcon className="w-5 h-5 text-neutral-400 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-neutral-700">
                            สถานะบัญชี
                          </div>
                          <div className="text-sm text-neutral-600">
                            {user.isActive ? (
                              <span className="text-green-600">ใช้งานอยู่</span>
                            ) : (
                              <span className="text-red-600">ไม่ได้ใช้งาน</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <KeyIcon className="w-5 h-5 text-neutral-400 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-neutral-700">
                            ระดับสิทธิ์
                          </div>
                          <div className="text-sm text-neutral-600">
                            {getRoleLabel(user.role)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end space-x-3 pt-6 border-t border-neutral-200">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={handleCancel}
                          disabled={isSaving}
                          className="px-6 py-2.5 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors duration-200 disabled:opacity-50"
                        >
                          ยกเลิก
                        </button>
                        <button
                          type="submit"
                          disabled={isSaving}
                          className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
                        >
                          {isSaving ? 'กำลังบันทึก...' : 'บันทึกการเปลี่ยนแปลง'}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        แก้ไขข้อมูล
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
